import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { FilterRule, FilterField, FilterOperator, FilterLogic } from '@/types/adjustment-filters';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface FilterRuleBuilderProps {
  rules: FilterRule[];
  onChange: (rules: FilterRule[]) => void;
}

export const FilterRuleBuilder: React.FC<FilterRuleBuilderProps> = ({ rules, onChange }) => {
  const { data: categories = [] } = useQuery({
    queryKey: ['supplier-categories'],
    queryFn: async () => {
      const { data } = await supabase
        .from('supplier_categories')
        .select('id, name')
        .order('name');
      return data || [];
    }
  });

  const { data: trades = [] } = useQuery({
    queryKey: ['supplier-trades'],
    queryFn: async () => {
      const { data } = await supabase
        .from('supplier_trades')
        .select('id, name')
        .order('name');
      return data || [];
    }
  });

  const addRule = () => {
    const newRule: FilterRule = {
      id: crypto.randomUUID(),
      field: 'category',
      operator: '=',
      value: '',
      logic: 'AND'
    };
    onChange([...rules, newRule]);
  };

  const removeRule = (ruleId: string) => {
    onChange(rules.filter(r => r.id !== ruleId));
  };

  const updateRule = (ruleId: string, updates: Partial<FilterRule>) => {
    onChange(rules.map(r => r.id === ruleId ? { ...r, ...updates } : r));
  };

  const getOperatorsForField = (field: FilterField): FilterOperator[] => {
    if (field === 'category' || field === 'trade') {
      return ['='];
    }
    return ['=', '>', '<', '>=', '<=', 'between'];
  };

  const renderValueInput = (rule: FilterRule) => {
    if (rule.field === 'category') {
      return (
        <Select value={rule.value as string} onValueChange={(value) => updateRule(rule.id, { value })}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (rule.field === 'trade') {
      return (
        <Select value={rule.value as string} onValueChange={(value) => updateRule(rule.id, { value })}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Select trade" />
          </SelectTrigger>
          <SelectContent>
            {trades.map(trade => (
              <SelectItem key={trade.id} value={trade.id}>{trade.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (rule.operator === 'between') {
      const values = Array.isArray(rule.value) ? rule.value : [0, 0];
      return (
        <div className="flex gap-2 items-center">
          <Input
            type="number"
            value={values[0] ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              updateRule(rule.id, { value: [val === '' ? ('' as any) : (parseFloat(val) || 0), values[1]] });
            }}
            onBlur={() => { if ((values[0] as any) === '') updateRule(rule.id, { value: [0, values[1]] }); }}
            className="w-[100px]"
            placeholder="Min"
          />
          <span className="text-muted-foreground">to</span>
          <Input
            type="number"
            value={values[1] ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              updateRule(rule.id, { value: [values[0], val === '' ? ('' as any) : (parseFloat(val) || 0)] });
            }}
            onBlur={() => { if ((values[1] as any) === '') updateRule(rule.id, { value: [values[0], 0] }); }}
            className="w-[100px]"
            placeholder="Max"
          />
        </div>
      );
    }

    return (
      <Input
        type="number"
        value={(rule.value as number) ?? ''}
        onChange={(e) => {
          const val = e.target.value;
          updateRule(rule.id, { value: val === '' ? '' : (parseFloat(val) || 0) });
        }}
        onBlur={() => { if (rule.value === '') updateRule(rule.id, { value: 0 }); }}
        className="w-full sm:w-[150px]"
        placeholder="Value"
      />
    );
  };

  return (
    <div className="space-y-3">
      {(rules || []).map((rule, index) => (
        <div key={rule.id} className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 p-3 border rounded-lg bg-background">
            <Select 
              value={rule.field} 
              onValueChange={(value: FilterField) => {
                const operators = getOperatorsForField(value);
                updateRule(rule.id, { 
                  field: value, 
                  operator: operators[0],
                  value: value === 'category' || value === 'trade' ? '' : 0
                });
              }}
            >
              <SelectTrigger className="w-full sm:w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="category">Category</SelectItem>
                <SelectItem value="trade">Trade</SelectItem>
                <SelectItem value="quantity">Quantity</SelectItem>
                <SelectItem value="base_cost">Base Cost</SelectItem>
              </SelectContent>
            </Select>

            <Select 
              value={rule.operator} 
              onValueChange={(value: FilterOperator) => updateRule(rule.id, { operator: value })}
            >
              <SelectTrigger className="w-full sm:w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getOperatorsForField(rule.field).map(op => (
                  <SelectItem key={op} value={op}>
                    {op === '=' ? 'equals' : op === 'between' ? 'between' : op}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {renderValueInput(rule)}

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeRule(rule.id)}
              className="ml-auto"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {index < rules.length - 1 && (
            <div className="flex items-center gap-2 pl-4">
              <Select 
                value={rule.logic || 'AND'} 
                onValueChange={(value: FilterLogic) => updateRule(rule.id, { logic: value })}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">AND</SelectItem>
                  <SelectItem value="OR">OR</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">next condition</span>
            </div>
          )}
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRule}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Condition
      </Button>
    </div>
  );
};
